/**
 * หน้าจอบันทึกการเปลี่ยนสิทธิ์ — `GET /v1/admin/audit-log`
 *
 * **เป็นสมุดของ `rbac_audit_log` เท่านั้น ไม่ใช่ audit ของข้อมูลธุรกิจ** สองอย่าง
 * นี้เป็นคนละตารางที่คนละกลุ่มอ่าน (`docs/SECURITY.md`): การเปลี่ยนว่า *ใครอนุมัติ
 * ข้อมูลสินค้าได้* เป็นเหตุการณ์คนละชนิดกับการเปลี่ยน *ข้อมูลสินค้า* และการเทรวมกัน
 * จะกลบสายที่เล็กกว่าและอ่อนไหวกว่ามาก
 *
 * หน้าจอนี้ **อ่านอย่างเดียว** ไม่มีเส้นทางเขียนใด ๆ เลย — จึงถูก gate ด้วย
 * `RBAC_ADMIN` + `VIEW` ที่ระดับ route และไม่มีการ์ด `EDIT` ข้างใน เพราะไม่มีปุ่ม
 * ให้กัน
 *
 * สิ่งที่หน้าจอนี้ปฏิเสธที่จะทำ: **ตีความ snapshot แทนผู้อ่าน** `before_json`/
 * `after_json` ถูกแสดงตามที่เก็บ และ `null` ถูกแสดงเป็น "{@link NO_SNAPSHOT_LABEL}"
 * ซึ่งเป็นข้อเท็จจริง ("ตอนนั้นยังไม่มีสิ่งนี้อยู่") ไม่ใช่ช่องว่างที่อ่านเหมือน
 * ข้อมูลโหลดไม่ครบ
 */

import { useEffect, useState } from 'react';

import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import {
  NO_MODULE_LABEL,
  NO_SNAPSHOT_LABEL,
  NO_TARGET_LABEL,
  actionLabel,
  auditTargets,
  hasSnapshot,
  snapshotEntries,
} from './rbacAuditLogView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { fetchRbacAuditLog, type RbacAdminFailure } from '../../lib/rbacAdmin/client';
import type { AuditSnapshot, RbacAuditEntry } from '../../lib/rbacAdmin/types';

export interface RbacAuditLogScreenProps {
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type AuditLogState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly RbacAuditEntry[] }
  | RbacAdminFailure;

/** เซลล์ของ snapshot หนึ่งฝั่ง — `null` มีข้อความของตัวเอง ไม่ใช่ช่องว่าง */
function SnapshotCell({
  snapshot,
  testId,
}: {
  readonly snapshot: AuditSnapshot | null;
  readonly testId: string;
}) {
  if (!hasSnapshot(snapshot)) {
    return (
      <td data-testid={testId} data-snapshot="none">
        <span className="cap-field__hint">{NO_SNAPSHOT_LABEL}</span>
      </td>
    );
  }

  const entries = snapshotEntries(snapshot);
  if (entries.length === 0) {
    // object ว่างคือ "มีสถานะบันทึกไว้ และไม่มีฟิลด์เลย" — คนละเรื่องกับ `null`
    return (
      <td data-testid={testId} data-snapshot="empty">
        <span className="cap-field__hint">ไม่มีฟิลด์ในบันทึกนี้</span>
      </td>
    );
  }

  return (
    <td data-testid={testId} data-snapshot="present">
      {entries.map(([key, value]) => (
        <div key={key}>
          <code className="cap-table__code">{key}</code>{' '}
          <span className="cap-table__muted">{value}</span>
        </div>
      ))}
    </td>
  );
}

export function RbacAuditLogScreen({ apiBaseUrl, fetchImpl }: RbacAuditLogScreenProps) {
  const [state, setState] = useState<AuditLogState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchRbacAuditLog({ apiBaseUrl, fetchImpl, signal: controller.signal }).then(
      (result) => {
        if (!active || result.status === 'cancelled') {
          return;
        }
        setState(
          result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
        );
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  return (
    <div className="cap-page" data-testid="rbac-audit-log">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            บันทึกการเปลี่ยนสิทธิ์{' '}
            <InfoHint
              label="เกี่ยวกับหน้าบันทึกการเปลี่ยนสิทธิ์"
              text={
                <>
                  ทุกครั้งที่มีการเปลี่ยนว่า <strong>ใครทำอะไรได้</strong> — ติ๊กสิทธิ์ในตาราง
                  สิทธิ์ตามแผนก มอบ/ถอดบทบาทระบบ หรือสร้างแผนก/พนักงาน — จะมีหนึ่งบรรทัดที่นี่
                  เรียงใหม่สุดก่อน นี่ <strong>ไม่ใช่</strong> บันทึกการแก้ข้อมูลธุรกิจ
                  ซึ่งเป็นคนละสมุดที่อ่านผ่านหน้าจอของแต่ละโมดูล
                </>
              }
            />
          </h1>
        </div>
      </div>

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="rbac-audit-log-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดบันทึกการเปลี่ยนสิทธิ์…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={state}
          context="audit-log"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-audit-log-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีรายการ</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีบันทึกการเปลี่ยนสิทธิ์</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่สมุดว่างเปล่า — <strong>ไม่ใช่การโหลดที่ล้มเหลว</strong>{' '}
            ตารางนี้เพิ่มบรรทัดเฉพาะเมื่อมีคนเปลี่ยนสิทธิ์ บทบาท หรือข้อมูลหลักขององค์กร
            ซึ่งเป็นการกระทำที่ตั้งใจและเกิดไม่บ่อย
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="บันทึกการเปลี่ยนสิทธิ์">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              รายการล่าสุด {state.items.length} รายการ{' '}
              <InfoHint
                label="เกี่ยวกับลำดับของรายการ"
                text="เรียงใหม่สุดก่อนตามที่เซิร์ฟเวอร์ส่งมา — หน้าจอไม่จัดลำดับใหม่เอง"
              />
            </h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="rbac-audit-log-table">
              <caption className="cap-visually-hidden">
                บันทึกการเปลี่ยนสิทธิ์ทุกบรรทัด: ใครเป็นผู้ทำ เปลี่ยนของใครหรือของแผนกใด
                โมดูลไหน การกระทำแบบใด พร้อมสถานะก่อนและหลัง
              </caption>
              <thead>
                <tr>
                  <th scope="col">เวลา</th>
                  <th scope="col">ผู้ทำรายการ</th>
                  <th scope="col">เป้าหมาย</th>
                  <th scope="col">โมดูล</th>
                  <th scope="col">การกระทำ</th>
                  <th scope="col">ก่อน</th>
                  <th scope="col">หลัง</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((entry) => {
                  const targets = auditTargets(entry);
                  return (
                    <tr key={entry.audit_id} data-testid={`rbac-audit-row-${entry.audit_id}`}>
                      <th scope="row" className="cap-table__row-head">
                        {entry.created_at}
                      </th>
                      <td data-testid={`rbac-audit-actor-${entry.audit_id}`}>
                        <code className="cap-table__code">{entry.actor_employee_id}</code>
                      </td>
                      <td data-testid={`rbac-audit-target-${entry.audit_id}`}>
                        {targets.length === 0 ? (
                          <span className="cap-field__hint">{NO_TARGET_LABEL}</span>
                        ) : (
                          targets.map((target) => (
                            <span key={target.kind}>
                              {target.label}{' '}
                              <code className="cap-table__code">{target.value}</code>
                            </span>
                          ))
                        )}
                      </td>
                      <td data-testid={`rbac-audit-module-${entry.audit_id}`}>
                        {entry.module_code === null ? (
                          <span className="cap-field__hint">{NO_MODULE_LABEL}</span>
                        ) : (
                          <code className="cap-table__code">{entry.module_code}</code>
                        )}
                      </td>
                      <td data-testid={`rbac-audit-action-${entry.audit_id}`}>
                        <StatusBadge tone="info">{actionLabel(entry.action)}</StatusBadge>
                      </td>
                      <SnapshotCell
                        snapshot={entry.before_json}
                        testId={`rbac-audit-before-${entry.audit_id}`}
                      />
                      <SnapshotCell
                        snapshot={entry.after_json}
                        testId={`rbac-audit-after-${entry.audit_id}`}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
